import type { ProjectVersion_Insert_Input } from "@/graphql";
import type {
  ProjectCalculationInput,
  ProjectLocationWriteInput,
  ProjectSpecialConditionConfigInput,
  ProjectSpeckleParameterConfigInput,
} from "../types/ProjectSetupWriteTypes";

// Trail rows for the project-setup writes.
//
// entityType is "project" for the location and calculation rows, not a new "calculation" or "location" value:
// project_version_entity_type_check is a Postgres CHECK constraint listing eight subjects, so
// anything outside it is rejected outright and widening it would need a Hasura migration. Both
// changes ARE project base information, and the history page already files project rows under its
// Base info chip.
//
// Neither summary may contain the word "revision": isRevisionEvent in projectVersion.types.ts is
// `entityType === "project" && /\brevisions?\b/i.test(summary)`, so a stray mention would file the
// row under the Team chip instead.
//
// createdBy, tenantId and source are permission presets — setting them here would be rejected.

export interface VersionMeta {
  operation: "create" | "update";
  entityLabel: string;
}

/**
 * The address rail's meta. `previous` is what the project's address was before this write, or null
 * when there is no baseline to diff against (the create wizard's first save) — the same shape the
 * building-area rail takes, and for the same reason: a trail row that says what a field moved FROM
 * is worth twice one that only says where it landed.
 */
export interface LocationVersionMeta {
  operation: "create" | "update";
  previous: ProjectLocationWriteInput | null;
}

export const buildCalculationVersionInput = (input: ProjectCalculationInput, meta: VersionMeta): ProjectVersion_Insert_Input => ({
  projectId: input.projectId,
  entityType: "project",
  operation: meta.operation,
  summary: meta.operation === "create" ? "Calculation assumptions set" : "Calculation assumptions updated",
  entityLabel: meta.entityLabel,
  changes: [
    { field: "Calculation type", from: null, to: meta.entityLabel },
    { field: "Consideration period", from: null, to: String(input.considerationPeriod), unit: "years" },
    { field: "Threshold limit", from: null, to: String(input.thresholdLimit) },
    ...(input.installationThresholdLimit == null
      ? []
      : [{ field: "Construction limit", from: null, to: String(input.installationThresholdLimit) }]),
    { field: "Included stages", from: null, to: input.stageIds.join(", ") },
  ],
});

/**
 * The three fields the history page names, in display order, each carrying the value it is written
 * with. The street number rides in "Street" and the postal code in "City" because that is how the
 * address reads to a human, and the two columns behind each of them never move apart.
 */
const locationFields = (input: ProjectLocationWriteInput): { field: string; value: string }[] => [
  { field: "Street", value: [input.street, input.streetNumber].filter(Boolean).join(" ") },
  { field: "City", value: [input.postalCode, input.city].filter(Boolean).join(" ") },
  { field: "Country", value: input.country ?? "" },
];

/**
 * The trail row for the address.
 *
 * Only the fields that actually MOVED are listed (RTLCA-5816). Listing all three with `from: null`
 * described the wrong change twice over: the row for a project whose country was set at creation and
 * whose street was filled in later claimed the country had just arrived from nothing, and the row
 * for the creation itself listed an empty Street and City as `—` → `—`, which is not an edit anybody
 * made. Paired by index before filtering, like the building-area rail — the filtered list has no
 * index left to look a baseline up by.
 *
 * The summary follows the same evidence: a write that touched nothing but the country says so, so
 * the country the start dialog seeds no longer reads as a whole address being set.
 */
export const buildLocationVersionInput = (
  input: ProjectLocationWriteInput,
  meta: LocationVersionMeta
): ProjectVersion_Insert_Input => {
  const next = locationFields(input);
  const previous = meta.previous === null ? null : locationFields(meta.previous);
  const label = next
    .map((entry) => entry.value)
    .filter(Boolean)
    .join(", ");

  const changes = next
    // No baseline is "" rather than null: every one of these columns is NOT NULL, so the state before
    // a first write is an empty string, and rendering it as `—` is what the history page already does.
    .map((entry, index) => ({ entry, previousValue: previous === null ? "" : previous[index].value }))
    .filter(({ entry, previousValue }) => previousValue !== entry.value)
    .map(({ entry, previousValue }) => ({ field: entry.field, from: previousValue, to: entry.value }));

  const countryOnly = changes.length === 1 && changes[0].field === "Country";
  const subject = countryOnly ? "country" : "address";

  return {
    projectId: input.projectId,
    entityType: "project",
    operation: meta.operation,
    summary: `Project ${subject} ${meta.operation === "create" ? "set" : "updated"}`,
    entityLabel: label,
    changes,
  };
};

/**
 * The trail row for the special-conditions toggle.
 *
 * `entityType` is "special_condition" and NOT "project" like the two above, because that value is
 * already in the check constraint and the API writes the same one when this flag travels in the REST
 * payload — matching it is what keeps one user action from appearing under two different chips
 * depending on which rail happened to carry it. An environment whose constraint predates that value
 * rejects the insert, which is exactly what `isVersionEntityTypeUnsupported` is for.
 *
 * `from` is filled in here, unlike the two builders above: the flag has two states, so the previous
 * one is simply the other one, and a diff that says "Disabled → Enabled" is worth more than half of
 * it. Nothing else about the row is inferable — hence no attempt at it elsewhere.
 */
export const buildSpecialConditionVersionInput = (input: ProjectSpecialConditionConfigInput): ProjectVersion_Insert_Input => ({
  projectId: input.projectId,
  entityType: "special_condition",
  operation: "update",
  summary: input.enabled ? "Special conditions enabled" : "Special conditions disabled",
  changes: [
    { field: "Special conditions", from: input.enabled ? "Disabled" : "Enabled", to: input.enabled ? "Enabled" : "Disabled" },
  ],
});

// "project", not "model": a model row is what the Speckle connector's own syncs write, and this is a setting.
export const buildSpeckleParameterVersionInput = (
  input: ProjectSpeckleParameterConfigInput,
  meta: Pick<VersionMeta, "entityLabel">
): ProjectVersion_Insert_Input => ({
  projectId: input.projectId,
  entityType: "project",
  operation: "update",
  summary: "Parameter mapping updated",
  entityLabel: meta.entityLabel,
  changes: [
    { field: "Classification system", from: null, to: meta.entityLabel },
    { field: "Parameter", from: null, to: input.classificationCode },
  ],
});
